// packages/modules/tests/shared/cyotMockData.ts
import type { UnscheduledTest, Exam, Schedule, ScoreTest } from "./types";

// Toggle for local testing.
// `true`  => show empty state on Tests/Attempted tabs
// `false` => show mocked cards
const TEST_EMPTY_STATE = false;

// Helper to create ISO timestamp relative to now
const minutesAgo = (minutes: number): string => {
  const now = Date.now();
  const past = now - minutes * 60 * 1000;
  return new Date(past).toISOString();
};

// Helper to create ISO timestamp in the future
const minutesFromNow = (minutes: number): string => {
  const now = Date.now();
  const future = now + minutes * 60 * 1000;
  return new Date(future).toISOString();
};

export const CYOT_MOCK_TESTS: UnscheduledTest[] = TEST_EMPTY_STATE
  ? []
  : [
      {
        id: "82571-1763137455",
        title: "CYOT - Biology Chapter 1",
        duration_in_minutes: 60,
        status: "not_attempted",
        subject: "Biology",
        student_marks: null,
        maximum_marks: null,
      },
      {
        id: "82572-1763137456",
        title: "CYOT - Chemistry Fundamentals",
        duration_in_minutes: 45,
        status: "not_attempted",
        subject: "Chemistry",
        student_marks: null,
        maximum_marks: null,
      },
      {
        id: "82573-1763137457",
        title: "CYOT - Physics Mechanics",
        duration_in_minutes: 90,
        status: "not_attempted",
        subject: "Physics",
        student_marks: null,
        maximum_marks: null,
      },
    ];

export const CYOT_MOCK_ATTEMPTED_TESTS: UnscheduledTest[] = TEST_EMPTY_STATE
  ? []
  : [
      {
        id: "82574-1763137458",
        title: "CYOT - Mathematics Algebra",
        duration_in_minutes: 75,
        status: "attempted",
        subject: "Mathematics",
        student_marks: 85,
        maximum_marks: 100,
      },
      {
        id: "82575-1763137459",
        title: "CYOT - Botany Plant Physiology",
        duration_in_minutes: 60,
        status: "attempted",
        subject: "Botany",
        student_marks: 72,
        maximum_marks: 90,
      },
    ];

// Mock resume tests for testing exam expiration
// Toggle this to test the expiration feature
const TEST_EXPIRATION_MODE = true;

export const CYOT_MOCK_RESUME_TESTS: UnscheduledTest[] = TEST_EXPIRATION_MODE
  ? [
      // Expires in ~2 minutes
      {
        id: "resume-expiring-soon-1",
        title: "CYOT - Expiring in 2 min",
        duration_in_minutes: 15,
        status: "in_progress",
        subject: "Biology",
        student_marks: null,
        maximum_marks: null,
        student_start_time: minutesAgo(14.8),
        exam_id: "mock-exam-1",
        question_paper_id: "mock-paper-1",
      },
      // Expires in ~30 seconds
      {
        id: "resume-expiring-soon-2",
        title: "CYOT - Expiring in 30 sec",
        duration_in_minutes: 15,
        status: "in_progress",
        subject: "Chemistry",
        student_marks: null,
        maximum_marks: null,
        student_start_time: minutesAgo(14.9),
        exam_id: "mock-exam-2",
        question_paper_id: "mock-paper-2",
      },
    ]
  : [];

// Mock resume tests for ILTS exams
export const ILTS_MOCK_RESUME_EXAMS: Exam[] = TEST_EXPIRATION_MODE
  ? [
      {
        id: "ilts-expiring-soon-1",
        title: "ILTS - Physics Expiring in 15 sec",
        duration_in_minutes: 20,
        status: "in_progress",
        student_start_time: minutesAgo(19.9), // 0.25 min = 15 sec remaining
        student_end_time: null,
        student_marks: null,
        maximum_marks: 100,
        exam_id: "ilts-exam-1",
        question_paper_id: "ilts-paper-1",
      },
      {
        id: "ilts-expiring-soon-2",
        title: "ILTS - Chemistry Expiring in 30 sec",
        duration_in_minutes: 15,
        status: "in_progress",
        student_start_time: minutesAgo(14.5),
        student_end_time: null,
        student_marks: null,
        maximum_marks: 80,
        exam_id: "ilts-exam-2",
        question_paper_id: "ilts-paper-2",
      },
    ]
  : [];

// Mock resume tests for SCORE exams (matching ScoreTest interface)
export const SCORE_MOCK_RESUME_TESTS: ScoreTest[] = TEST_EXPIRATION_MODE
  ? [
      {
        id: "score-expiring-soon-1",
        examid: "score-exam-1",
        grade: "11",
        exam: "JEEMAIN",
        title: "SCORE - JEE Main Expiring in 25 sec",
        status: "in_progress",
        startDate: minutesAgo(11.5), // Started 11.5 min ago, will expire in 0.5 min (30 sec)
        endDate: minutesFromNow(0.5), // Will end in 30 sec
        duration: 12, // SCORE uses duration field in minutes
        totalQuestions: 50,
        correctMarks: 0,
        incorrectMarks: 0,
        registeredCount: "1",
        student_end_time: null,
        questionTypes: ["Single Correct MCQ", ""],
      },
      {
        id: "score-expiring-soon-2",
        examid: "score-exam-2",
        grade: "11",
        exam: "JEEMAIN",
        title: "SCORE - JEE Main Expiring in 45 sec",
        status: "in_progress",
        startDate: minutesAgo(9.25), // Started 9.25 min ago, will expire in 0.75 min (45 sec)
        endDate: minutesFromNow(0.76), // Will end in 45 sec
        duration: 10,
        totalQuestions: 40,
        correctMarks: 0,
        incorrectMarks: 0,
        registeredCount: "1",
        student_end_time: null,
        questionTypes: ["Single Correct MCQ", ""],
      },
    ]
  : [];

// Mock resume tests for MCT exams
export const MCT_MOCK_RESUME_TESTS: UnscheduledTest[] = TEST_EXPIRATION_MODE
  ? [
      {
        id: "mct-expiring-soon-1",
        title: "MCT - Biology Expiring in 12 sec",
        duration_in_minutes: 8,
        status: "in_progress",
        subject: "Biology",
        student_marks: null,
        maximum_marks: 50,
        student_start_time: minutesAgo(7.91), // 0.2 min = 12 sec remaining
        exam_id: "mct-exam-1",
        question_paper_id: "mct-paper-1",
      },
      {
        id: "mct-expiring-soon-2",
        title: "MCT - Chemistry Expiring in 35 sec",
        duration_in_minutes: 10,
        status: "in_progress",
        subject: "Chemistry",
        student_marks: null,
        maximum_marks: 60,
        student_start_time: minutesAgo(9.95), // 0.58 min = 35 sec remaining
        exam_id: "mct-exam-2",
        question_paper_id: "mct-paper-2",
      },
    ]
  : [];

// Mock resume schedules for ILTS (nested structure)
export const ILTS_MOCK_RESUME_SCHEDULES: Schedule[] = TEST_EXPIRATION_MODE
  ? [
      {
        id: "ilts-schedule-1",
        title: "ILTS Mock Schedule",
        slots: [
          {
            id: "ilts-slot-1",
            title: "Physics Slot",
            exams: ILTS_MOCK_RESUME_EXAMS,
          },
        ],
      },
    ]
  : [];

/**
 * Local SCORE mock — no EOS backend required.
 *
 * 1. Set SCORE_MOCK_MODE = true and pick SCORE_MOCK_SCENARIO below.
 * 2. Restart dev server (store initializes at module load).
 * 3. Test SCOREPage / SCOREHomeScreen tabs + ongoing section.
 *
 * Focused scenarios (one card state per scenario):
 *
 * | Scenario                         | Section / tab      | Button           | Expected action              |
 * | -------------------------------- | ------------------ | ---------------- | ---------------------------- |
 * | liveUnregisteredNoMissed         | Ongoing            | Attempt          | start_exam (+ register toast)|
 * | liveUnregisteredWithMissed       | Ongoing + Missed   | Attempt          | open_reschedule_latest       |
 * | liveUnregisteredSyllabus         | Ongoing            | Syllabus         | open_syllabus                |
 * | upcomingUnregisteredNoMissed     | Upcoming           | Register Now     | register_only                |
 * | upcomingUnregisteredWithMissed   | Upcoming + Missed  | Register Now     | open_reschedule_latest       |
 * | missedOnly                       | Missed             | Reschedule       | open_reschedule_self         |
 * | completedOnly                    | Attempted          | View Report / Cert | view_report / open_certificate |
 *
 * Combo scenarios (multiple cards):
 * | withMissed                       | Missed + Upcoming + unregistered slot |
 * | noMissedLiveUnregistered         | alias of liveUnregisteredNoMissed     |
 * | noMissedLiveRegistered           | Live registered → Attempt → instructions |
 * | noMissedInProgress               | In-progress → Resume                  |
 */
export const SCORE_MOCK_MODE = false;

export type ScoreMockScenario =
  | "liveUnregisteredNoMissed"
  | "liveUnregisteredWithMissed"
  | "liveUnregisteredSyllabus"
  | "upcomingUnregisteredNoMissed"
  | "upcomingUnregisteredWithMissed"
  | "missedOnly"
  | "completedOnly"
  | "withMissed"
  | "noMissedLiveUnregistered"
  | "noMissedLiveRegistered"
  | "noMissedInProgress";

export const SCORE_MOCK_SCENARIO: ScoreMockScenario =
  "liveUnregisteredNoMissed";

export function createScoreMockRecordsForScenario(
  scenario: ScoreMockScenario,
): {
  ongoing: ScoreMockSlot[];
  upcoming: ScoreMockSlot[];
  attempted: ScoreMockSlot[];
  missed: ScoreMockSlot[];
  slots: ScoreMockSlot[];
} {
  const baseSlot = (
    id: string,
    title: string,
    datetime: string,
    enddatetime: string,
    extra: Partial<ScoreMockSlot> = {},
  ): ScoreMockSlot => ({
    _id: id,
    status: "open",
    title,
    gradename: "11",
    examtype: "NEET",
    examid: `${id}-exam`,
    questionpaperid: `${id}-paper`,
    duration_in_minutes: 120,
    datetime,
    enddatetime,
    date: datetime.slice(0, 10),
    timeslot: "10:00 AM",
    endtimeslot: "12:00 PM",
    registeredCount: 500,
    scoreExamData: {
      grade: 11,
      examtype: "NEET",
      duration: "120",
      questioncount: 60,
      positivemarks: 4,
      negativemarks: -1,
      questionTypes: ["Single Correct MCQ"],
    },
    ...extra,
  });

  const missedSlot = baseSlot(
    "score-mock-missed",
    "SCORE Scholarship Exam 2026",
    minutesAgo(48 * 60),
    minutesAgo(46 * 60),
  );

  const upcomingRegistered = baseSlot(
    "score-mock-upcoming-reg",
    "SCORE Grade 11 NEET",
    minutesFromNow(360),
    minutesFromNow(480),
  );

  const upcomingUnregistered = baseSlot(
    "score-mock-upcoming-unreg",
    "SCORE Scholarship Exam 2026",
    minutesFromNow(720),
    minutesFromNow(840),
    { examid: "", questionpaperid: "" },
  );

  const liveRegistered = baseSlot(
    "score-mock-live-reg",
    "SCORE Scholarship Exam 2026",
    minutesAgo(2),
    minutesFromNow(118),
  );

  const liveUnregistered = baseSlot(
    "score-mock-live-unreg",
    "SCORE Scholarship Exam 2026",
    minutesAgo(1),
    minutesFromNow(119),
    { examid: "", questionpaperid: "" },
  );

  const inProgress = baseSlot(
    "score-mock-in-progress",
    "SCORE Scholarship Exam 2026",
    minutesAgo(30),
    minutesFromNow(90),
    {
      isExamInProgress: true,
      started_at: minutesAgo(10),
    },
  );

  const attempted = baseSlot(
    "score-mock-attempted",
    "SCORE Scholarship Exam 2026",
    minutesAgo(72 * 60),
    minutesAgo(70 * 60),
    {
      isExamSubmittedByUser: true,
      certificateUrl: "https://example.com/cert.pdf",
    },
  );

  const liveUnregisteredOnly = {
    missed: [] as ScoreMockSlot[],
    upcoming: [] as ScoreMockSlot[],
    ongoing: [] as ScoreMockSlot[],
    attempted: [] as ScoreMockSlot[],
    slots: [liveUnregistered],
  };

  const upcomingUnregisteredOnly = {
    missed: [] as ScoreMockSlot[],
    upcoming: [] as ScoreMockSlot[],
    ongoing: [] as ScoreMockSlot[],
    attempted: [] as ScoreMockSlot[],
    slots: [upcomingUnregistered],
  };

  switch (scenario) {
    case "liveUnregisteredNoMissed":
    case "liveUnregisteredSyllabus":
    case "noMissedLiveUnregistered":
      return liveUnregisteredOnly;
    case "liveUnregisteredWithMissed":
      return {
        ...liveUnregisteredOnly,
        missed: [missedSlot],
      };
    case "upcomingUnregisteredNoMissed":
      return upcomingUnregisteredOnly;
    case "upcomingUnregisteredWithMissed":
      return {
        ...upcomingUnregisteredOnly,
        missed: [missedSlot],
      };
    case "missedOnly":
      return {
        missed: [missedSlot],
        upcoming: [],
        ongoing: [],
        attempted: [],
        slots: [],
      };
    case "completedOnly":
      return {
        missed: [],
        upcoming: [],
        ongoing: [],
        attempted: [attempted],
        slots: [],
      };
    case "withMissed":
      return {
        missed: [missedSlot],
        upcoming: [upcomingRegistered],
        ongoing: [],
        attempted: [],
        slots: [upcomingUnregistered],
      };
    case "noMissedLiveRegistered":
      return {
        missed: [],
        upcoming: [],
        ongoing: [liveRegistered],
        attempted: [],
        slots: [],
      };
    case "noMissedInProgress":
      return {
        missed: [],
        upcoming: [],
        ongoing: [inProgress],
        attempted: [],
        slots: [],
      };
    default:
      return {
        missed: [],
        upcoming: [upcomingRegistered],
        ongoing: [],
        attempted: [attempted],
        slots: [upcomingUnregistered],
      };
  }
}

// Toggle to inject SCORE registered slots into the API conversion layer
// (useSCORETests) so the registered → live transition can be exercised end-to-end:
//   - 5 min before start: should appear as Live in the resume section
//   - 30 min before start: should remain Registered in the upcoming list
//   - currently mid-window: should appear as Live in the resume section
const SCORE_LIVE_TEST_MODE = false;

/** Raw slot shape consumed by `mapSlotToScoreTest` in `useSCORETests`. Loosely
 * typed so it can be injected directly into the `upcoming` records array. */
export interface ScoreMockSlot {
  _id: string;
  status: string;
  title: string;
  grade?: string;
  gradename?: string;
  examtype?: string;
  examid: string;
  questionpaperid: string;
  duration_in_minutes: number;
  datetime: string;
  enddatetime: string;
  date?: string;
  timeslot?: string;
  endtimeslot?: string;
  registeredCount?: number;
  isExamInProgress?: boolean;
  isExamSubmittedByUser?: boolean;
  started_at?: string;
  certificateUrl?: string;
  scoreExamData?: {
    grade?: number;
    examtype?: string;
    duration?: string;
    questioncount?: number;
    positivemarks?: number;
    negativemarks?: number;
    questionTypes?: string[];
  };
}

/** Mock SCORE upcoming slots for verifying the 10-min pre-start "live" window. */
export const SCORE_MOCK_UPCOMING_SLOTS: ScoreMockSlot[] = SCORE_LIVE_TEST_MODE
  ? [
      // Starts in 5 minutes — within the 10-min live window
      {
        _id: "score-mock-live-soon",
        status: "open",
        title: "SCORE Mock — Live in 5 min",
        gradename: "11",
        examtype: "JEEMAIN",
        examid: "score-mock-exam-soon",
        questionpaperid: "score-mock-paper-soon",
        duration_in_minutes: 60,
        datetime: minutesFromNow(5),
        enddatetime: minutesFromNow(65),
        registeredCount: 1240,
        scoreExamData: {
          grade: 11,
          examtype: "JEEMAIN",
          duration: "60",
          questioncount: 50,
          positivemarks: 4,
          negativemarks: -1,
          questionTypes: ["Single Correct MCQ"],
        },
      },
      // Starts in 30 minutes — outside the 10-min live window (stays Registered)
      {
        _id: "score-mock-upcoming",
        status: "open",
        title: "SCORE Mock — Upcoming in 30 min",
        gradename: "11",
        examtype: "NEET",
        examid: "score-mock-exam-upcoming",
        questionpaperid: "score-mock-paper-upcoming",
        duration_in_minutes: 90,
        datetime: minutesFromNow(30),
        enddatetime: minutesFromNow(120),
        registeredCount: 875,
        scoreExamData: {
          grade: 11,
          examtype: "NEET",
          duration: "90",
          questioncount: 60,
          positivemarks: 4,
          negativemarks: -1,
          questionTypes: ["Single Correct MCQ"],
        },
      },
      // Already in time window — should be Live
      {
        _id: "score-mock-now-live",
        status: "open",
        title: "SCORE Mock — Currently Live",
        gradename: "12",
        examtype: "JEEADV",
        examid: "score-mock-exam-now",
        questionpaperid: "score-mock-paper-now",
        duration_in_minutes: 120,
        datetime: minutesAgo(2),
        enddatetime: minutesFromNow(118),
        registeredCount: 2300,
        scoreExamData: {
          grade: 12,
          examtype: "JEEADV",
          duration: "120",
          questioncount: 75,
          positivemarks: 4,
          negativemarks: -1,
          questionTypes: ["Single Correct MCQ"],
        },
      },
    ]
  : [];
